import { driverLedgerListQuerySchema, type DriverLedgerEntryInput, type DriverLedgerEntryView, type Page } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { ApiError } from '../../../shared/shared.api';
import { listDriverLedger, recordDriverLedgerEntry } from '../drivers.api';

export type DriverLedgerState = { status: 'loading' } | { status: 'loaded'; page: Page<DriverLedgerEntryView> } | { status: 'error' };

/** The page of a driver's ledger the URL's list query asks for, and recording a finance entry. `onRecorded` lets the page refresh the balance. */
export function useDriverLedger(driverId: string, onRecorded: () => Promise<void> | void) {
  const list = useListQuery(driverLedgerListQuerySchema);
  const { query } = list;
  const toast = useToast();
  const [state, setState] = useState<DriverLedgerState>({ status: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listDriverLedger(driverId, query)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [driverId, query, reloads]);

  const record = useCallback(
    async (input: DriverLedgerEntryInput): Promise<boolean> => {
      setRecordError(null);
      setRecording(true);
      try {
        await recordDriverLedgerEntry(driverId, input);
        setReloads((count) => count + 1);
        await onRecorded();
        toast.success('Ledger entry recorded');
        return true;
      } catch (err) {
        setRecordError((err instanceof ApiError ? apiErrorMessage(err) : undefined) ?? 'Could not record the entry — check the fields and try again.');
        return false;
      } finally {
        setRecording(false);
      }
    },
    [driverId, onRecorded, toast],
  );

  return { state, list, record, recording, recordError, clearRecordError: () => setRecordError(null) };
}
